import { forwardRef, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Label } from '@/shared/components/ui/Label/Label';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/RadioGroup/RadioGroup';
import { cn } from '@/shared/utils/cn/cn.util';

import {
  CHOICE_CARDS_CARD_CLASSES,
  CHOICE_CARDS_CARD_TEXT_CLASSES,
  CHOICE_CARDS_DESCRIPTION_CLASSES,
  CHOICE_CARDS_GROUP_CLASSES,
  SELECTION_NOTICE_CLASSES,
} from '../selection.tokens';

import { type ChoiceCardsProps, type ChoiceCardsRow } from './ChoiceCards.types';
import { useChoiceCardsLogic } from './useChoiceCardsLogic';

/**
 * Mutually exclusive choices, laid out as cards rather than collapsed behind
 * a trigger or stacked as bare radios — for exactly the case `Select` and a
 * plain `RadioGroup` both fit badly: a handful of options whose HINTS are the
 * thing an operator is comparing, so all of them stay visible at once.
 *
 * Lives in `selection/` beside `ToggleGroup` and `MultiSelect` for the same
 * reason those do: one announcer, one read-only contract, one set of tokens.
 *
 * **It forwards its ref to the FIRST card's radio**, so react-hook-form's
 * `shouldFocusError` can reach it — the same gap `ToggleGroup`'s own comment
 * describes for the three kinds that used to take no ref at all.
 */
export const ChoiceCards = forwardRef<HTMLButtonElement, ChoiceCardsProps>(function ChoiceCards(
  {
    id,
    className,
    label,
    items,
    value,
    onValueChange,
    isDisabled = false,
    isReadOnly = false,
    onBlur,
    'aria-describedby': describedBy,
  },
  ref,
) {
  const { t } = useTranslation('uiKit');
  const generatedId = useId();
  const groupId = id ?? generatedId;
  const { rows, isEmpty, select, announcement } = useChoiceCardsLogic({
    items,
    onValueChange,
    isDisabled,
    isReadOnly,
  });

  if (isEmpty) {
    return <p className={SELECTION_NOTICE_CLASSES}>{t('choiceCards.empty')}</p>;
  }

  return (
    <>
      <RadioGroup
        id={groupId}
        aria-label={label}
        aria-describedby={describedBy}
        // ARIA supports `aria-readonly` on `radiogroup`, not on `radio` — so it
        // goes here, once, exactly as `SelectionPanel`'s own single-select mode
        // already does.
        aria-readonly={isReadOnly ? true : undefined}
        // `''`, never `undefined`: Radix's `useControllableState` reads an
        // undefined `value` as UNCONTROLLED, which would make an unset policy
        // track its own first click instead of staying under this control.
        value={value ?? ''}
        onValueChange={select}
        onBlur={onBlur}
        className={cn(CHOICE_CARDS_GROUP_CLASSES, className)}
      >
        {rows.map((row, index) => (
          <ChoiceCard
            key={row.value}
            row={row}
            // Only the first card takes the ref: `shouldFocusError` calls
            // `focus()` once, and the top of the group is where reading starts.
            cardRef={index === 0 ? ref : undefined}
          />
        ))}
      </RadioGroup>

      {/* One live region for the whole control, always mounted — a region
          added with its first message is announced late or not at all. */}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </>
  );
});

interface ChoiceCardProps {
  row: ChoiceCardsRow;
  cardRef: React.Ref<HTMLButtonElement> | undefined;
}

/**
 * Module scope, not an inline closure: an identity created during render
 * remounts the radio on every pass, which React Compiler's `static-components`
 * rule forbids at error level — and here it would cost focus on every render.
 */
function ChoiceCard({ row, cardRef }: ChoiceCardProps) {
  const rowId = useId();
  const labelId = useId();
  const descriptionId = useId();

  return (
    // `<Label htmlFor>` still wraps the WHOLE card — a native label activates
    // its control from a click anywhere inside it, hint text included, so the
    // card stays one pointer target without a `<div onClick>` re-implementing
    // that for free behaviour (`SelectionOptionRow`'s own reasoning).
    //
    // The radio's `aria-labelledby` then overrides the implicit label-wrapping
    // name with the label span ALONE: an explicit `aria-labelledby` outranks
    // the host-language `<label for>` association in the accname algorithm, so
    // the hint reaches assistive tech as a DESCRIPTION rather than folding into
    // the name — "Always shown", not "Always shown Shown to everyone who…".
    <Label htmlFor={rowId} className={CHOICE_CARDS_CARD_CLASSES}>
      <RadioGroupItem
        ref={cardRef}
        id={rowId}
        value={row.value}
        disabled={row.isDisabled}
        aria-labelledby={labelId}
        aria-describedby={row.description === undefined ? undefined : descriptionId}
        className="mt-2xs shrink-0"
      />
      <span className={CHOICE_CARDS_CARD_TEXT_CLASSES}>
        <span id={labelId} className="text-foreground">
          {row.label}
        </span>
        {row.description === undefined ? null : (
          <span id={descriptionId} className={CHOICE_CARDS_DESCRIPTION_CLASSES}>
            {row.description}
          </span>
        )}
      </span>
    </Label>
  );
}
